import React, { useRef, useEffect, useMemo } from 'react';
import maplibregl from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import { useFullScreenHandle } from 'react-full-screen'
import { Stack } from '@mui/material';

import { PMTILES_URL } from '../const/general'
import { PMTILES_PREF_KEY } from '../config/config'

import {
    setTyphoonColor,
} from '../func/color';

import {
    StyleFullScreen,
    MapWrapper
} from '../style/map_components';

import MapFullscreenIcon from '../item/map_fullscreen_icon'
import '../style/legend_map.css';


const MapRiskAnalysis = (props) => {
    const sourceSetting = useMemo(() => {
        return {
            "osm": {
                "type": "raster",
                // "tiles": ["https://cyberjapandata.gsi.go.jp/xyz/seamlessphoto/{z}/{x}/{y}.jpg"],
                "tiles": ["https://cyberjapandata.gsi.go.jp/xyz/pale/{z}/{x}/{y}.png"],
                "tileSize": 256,
                "attribution": "MapLibre | 地図の出典：<a href='https://maps.gsi.go.jp/development/ichiran.html' target='_blank'>国土地理院</a>",
            },
            "city": {
                "type": "vector",
                "tiles": [`${PMTILES_URL}/${PMTILES_PREF_KEY}/city/{z}/{x}/{y}.mvt`],
            },
            "msh_50km": {
                "type": "vector",
                "tiles": [`${PMTILES_URL}/${PMTILES_PREF_KEY}/msh_50km/{z}/{x}/{y}.mvt`],
            },
        };
    }, [props.prefCode]);

    // 背景地図の設定
    const baseLayer = [
        {
            "id": "osm-tiles",
            "type": "raster",
            "source": "osm",
            "minzoom": 0,
            "maxzoom": 24,
        }
    ];

    // PMTilesから取得して表示、非表示を制御するレイヤ達  
    const otherLayers =
        [
            {
                "id": "msh_50km",
                "source": "msh_50km",
                "source-layer": "msh_50km",
                "type": "fill",
                "paint": {
                    "fill-color": 'rgba(146, 32, 32, 0)',
                    "fill-opacity": 0.7,
                    'fill-outline-color': 'rgba(0,0,0,0)',
                },
                "layout": {
                    "visibility": "visible" // 最初から表示する場合は "visible"に
                }
            },
            {
                "id": "city",
                "source": "city",
                "source-layer": "city",
                "type": "line",
                "paint": {
                    'line-opacity': 0.8,
                    'line-color': '#fff',
                    'line-width': 1.5,
                },
                "layout": {
                    "visibility": "visible" // 最初から表示する場合は "visible"に
                }
            },
        ];

    const mapContainer = useRef(null);
    const map = useRef(null);
    const markerRef = useRef(null);

    // 監視用Ref
    const selectCitysRef = useRef(props.selectCitys);
    const mapDataRef = useRef(props.mapData);

    const mapStyle = {
        height: '100%',
        width: '100%',
        margin: '0 auto',
    }

    // 全画面表示切り替え
    const fullscreenHandle = useFullScreenHandle()

    const layersSetting = [...baseLayer, ...otherLayers]

    let hoveredId = null


    // ベースレイヤーとなる市町村のラインを設定
    const setBaseCityLayer = () => {
        const matchExpression_line = ['match', ['get', 'id'], -1, 'rgba(0,0,0,0)'];
        if (!selectCitysRef.current || selectCitysRef.current === undefined || selectCitysRef.current === null || selectCitysRef.current.length < 1) {
            map.current.setPaintProperty('city', 'line-color', 'rgba(0,0,0,0)');
            return
        }
        for (const item of selectCitysRef.current) {
            matchExpression_line.push(item, '#fff')
        }
        matchExpression_line.push('rgba(0, 0, 0, 0)');
        map.current.setPaintProperty('city', 'line-color', matchExpression_line);
    }

    // メッシュのスタイル変更
    const setMshStyle = () => {

        // データがなければ透明化して終了
        if (!mapDataRef.current || mapDataRef.current === undefined || mapDataRef.current === null || mapDataRef.current.length < 1) {
            map.current.setPaintProperty('msh_50km', 'fill-color', 'rgba(0,0,0,0)');
            return;
        }

        // 色設定
        const matchExpression_fill = ['match', ['get', 'id'], -1, 'rgba(0,0,0,0)'];
        for (const item of mapDataRef.current) {
            if (item.code !== null && item.val !== null && item.val > 0) {
                matchExpression_fill.push(
                    Number(item.code),
                    setTyphoonColor(item.val).color);
            }
        }
        matchExpression_fill.push('rgba(0,0,0,0)');

        // 塗り
        map.current.setPaintProperty('msh_50km', 'fill-color', matchExpression_fill);
        map.current.setPaintProperty('msh_50km', 'fill-opacity', 0.7);
    };

    // レイヤーの色分け
    const setLayerStyle = () => {

        if (!map.current || !map.current.isStyleLoaded()) return;

        // ベースレイヤーとなる市町村のラインを設定
        setBaseCityLayer()

        // 台風の進路を全時間の最大値で表示
        setMshStyle()

    }

    // レイヤーマウスホバー時の処理
    const handleMouseMove = (e) => {

        const feature = e.features[0];
        if (!feature) return;
        const id = feature.properties.id;

        // すでにホバー中なら無視
        if (hoveredId === id) return;

        if (!mapDataRef.current || mapDataRef.current === undefined || mapDataRef.current === null || mapDataRef.current.length < 1) return

        const data = mapDataRef.current.find(item => item.code === Number(id));

        // 前のポップアップを削除
        if (map.current.__hoverPopup) {
            map.current.__hoverPopup.remove();
            map.current.__hoverPopup = null;
        }
        if (data.val > 0) {
            map.current.getCanvas().style.cursor = 'pointer';
        } else {
            map.current.getCanvas().style.cursor = '';
            return;
        }
        hoveredId = id;

        // ポップアップ表示
        if (data.val > 0) {
            const popup = new maplibregl.Popup({
                closeButton: false,
                closeOnClick: false
            })

                .setLngLat(e.lngLat)
                .setHTML(`
                        <div>台風名：<strong>${data.typhoon_name} </strong></div>
                        <div>確率：<strong>${data.val} %</strong></div>
                        <div>時間（最大時）：<strong>${data.max_datetime} </strong></div>
                    `)
                .addClassName('custom-popup')
                .addTo(map.current);

            map.current.__hoverPopup = popup;
        }
    };

    // レイヤーマウスアウト時の処理
    const handleMouseLeave = () => {
        map.current.getCanvas().style.cursor = '';
        if (map.current.__hoverPopup) {
            map.current.__hoverPopup.remove();
            map.current.__hoverPopup = null;
            hoveredId = null
        }
    };

    // ロード時の処理
    const handleLoad = () => {
        if (!map.current) return;
        setLayerStyle()
    };

    // マップ初期化処理
    useEffect(() => {
        if (map.current) return; // initialize map only once

        map.current = new maplibregl.Map({
            container: mapContainer.current,
            center: props.currentLngLat,
            zoom: 3,
            style: {
                version: 8,
                sources: sourceSetting,
                layers: layersSetting
            },
            attributionControl: false
        });
        map.current.addControl(new maplibregl.NavigationControl({
            // コンパス表示なし
            showCompass: false,
            showZoom: true,
        }), 'top-left');
        map.current.addControl(new maplibregl.AttributionControl({
            // Attributionは折りたたまない
            compact: false,
        }), 'bottom-right');

        map.current.on('load', handleLoad);
        map.current.on('mouseenter', 'msh_50km', (e) => { handleMouseMove(e) });
        map.current.on('mousemove', 'msh_50km', (e) => { handleMouseMove(e) });
        map.current.on('mouseleave', 'msh_50km', handleMouseLeave);

        return () => {
            if (!map.current) return;
            map.current.off('load', handleLoad);
            map.current.off('mouseenter', 'msh_50km', (e) => { handleMouseMove(e) });
            map.current.off('mousemove', 'msh_50km', (e) => { handleMouseMove(e) });
            map.current.off('mouseleave', 'msh_50km', handleMouseLeave);

            map.current.off('styledata', setLayerStyle());
            // map.current.getCanvas().removeEventListener('mouseleave', handleMouseLeave);
            map.current.remove();
            map.current = null;
        };
    }, []);

    // 市町村チェックボックス変更時に呼ばれる
    useEffect(() => {

        selectCitysRef.current = props.selectCitys

        if (mapDataRef.current && mapDataRef.current !== undefined && mapDataRef.current !== null && mapDataRef.current.length > 0) {
            if (map.current?.isStyleLoaded()) {
                setLayerStyle();
            }
        }
    }, [props.selectCitys]);

    // データ更新時
    useEffect(() => {

        mapDataRef.current = props.mapData

        if (!map.current) return;
        const interval = setInterval(() => {
            if (map.current?.isStyleLoaded()) {
                setLayerStyle();
                clearInterval(interval);
            }
        }, 100);

        return () => clearInterval(interval);
    }, [props.mapData]);

    return (
        <StyleFullScreen
            handle={fullscreenHandle}
            enabled={true}
        >
            <Stack direction="column"
                sx={{ height: '100%', minHeight: 0 }} >
                <MapWrapper sx={{ flex: 1, minHeight: 0 }}>
                    <div
                        ref={mapContainer}
                        className=""
                        style={mapStyle} />
                    <MapFullscreenIcon
                        fullscreenHandle={fullscreenHandle}
                        top={'10px'}
                    />
                </MapWrapper>
            </Stack>
        </StyleFullScreen>
    );
};

export default MapRiskAnalysis;

